import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { StaticImage } from 'gatsby-plugin-image';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import { useForm } from 'react-hook-form';
import ModalVideo from 'react-modal-video';
import { useSelector, useDispatch } from 'react-redux';
import { saveUser } from '@redux/user/actions';
import Form from 'react-bootstrap/Form';
import { Accordion } from 'react-bootstrap';
import Query from '@queries/snapshot/Snapshot';
import { isBrowser } from '@helpers/utils';

import SEO from '@common/SEO';
import Main from '@common/Main';
import OpenModalButton from '@common/OpenModalButton';
import ContextAwareToggle from '@common/accordion/ContextAwareToggle';
import Section from '@common/Section';
import Container from '@common/Container';
import Blocks from '@common/Blocks';
import Row from '@common/Row';
import Heading from '@common/Heading';
import Button from '@common/Button';
import MarketoForm from '@common/MarketoForm';

import Increase from '@images/snapshot-report/increase.svg';
import Message from '@images/snapshot-report/message.svg';
import Apple from '@images/snapshot-report/apple.svg';
import City from '@images/snapshot-report/city.svg';
import Little from '@images/snapshot-report/little.svg';
import YellowCircleStar from '@images/snapshot-report/yellow_circle_star.svg';

import Accounting from '@images/snapshot-report/accounting.svg';
import Billing from '@images/snapshot-report/billing.svg';
import Pen from '@images/snapshot-report/pen.svg';
import Other from '@images/snapshot-report/other.svg';

import ParticipantsRoles from '@images/snapshot-report/participants_roles.svg';
import ParticipantsLocations from '@images/snapshot-report/participants_locations.svg';
import CentersEnrollmentCapacity from '@images/snapshot-report/centers_enrollment_capacity.svg';

import * as styles from '@styles/modules/snapshot.module.scss';
import { stickyNav, stickyNavFixed, navLinks } from '@styles/modules/faq.module.scss';

const states = [
  {key: 'Outside of United States & Canada', value: 'International'},
  {key: 'Alabama', value: 'Alabama'},
  {key: 'Alaska', value: 'Alaska'},
  {key: 'American Samoa', value: 'American Samoa'},
  {key: 'Arizona', value: 'Arizona'},
  {key: 'Arkansas', value: 'Arkansas'},
  {key: 'California', value: 'California'},
  {key: 'Colorado', value: 'Colorado'},
  {key: 'Connecticut', value: 'Connecticut'},
  {key: 'Delaware', value: 'Delaware'},
  {key: 'District of Columbia', value: 'District of Columbia'},
  {key: 'Florida', value: 'Florida'},
  {key: 'Georgia', value: 'Georgia'},
  {key: 'Guam', value: 'Guam'},
  {key: 'Hawaii', value: 'Hawaii'},
  {key: 'Idaho', value: 'Idaho'},
  {key: 'Illinois', value: 'Illinois'},
  {key: 'Indiana', value: 'Indiana'},
  {key: 'Iowa', value: 'Iowa'},
  {key: 'Kansas', value: 'Kansas'},
  {key: 'Kentucky', value: 'Kentucky'},
  {key: 'Louisiana', value: 'Louisiana'},
  {key: 'Maine', value: 'Maine'},
  {key: 'Maryland', value: 'Maryland'},
  {key: 'Massachusetts', value: 'Massachusetts'},
  {key: 'Michigan', value: 'Michigan'},
  {key: 'Minnesota', value: 'Minnesota'},
  {key: 'Mississippi', value: 'Mississippi'},
  {key: 'Missouri', value: 'Missouri'},
  {key: 'Montana', value: 'Montana'},
  {key: 'Nebraska', value: 'Nebraska'},
  {key: 'Nevada', value: 'Nevada'},
  {key: 'New Hampshire', value: 'New Hampshire'},
  {key: 'New Jersey', value: 'New Jersey'},
  {key: 'New Mexico', value: 'New Mexico'},
  {key: 'New York', value: 'New York'},
  {key: 'North Carolina', value: 'North Carolina'},
  {key: 'North Dakota', value: 'North Dakota'},
  {key: 'North Carolina', value: 'North Carolina'},
  {key: 'Northern Mariana Islands', value: 'Northern Mariana Islands'},
  {key: 'Ohio', value: 'Ohio'},
  {key: 'Oklahoma', value: 'Oklahoma'},
  {key: 'Oregon', value: 'Oregon'},
  {key: 'Pennsylvania', value: 'Pennsylvania'},
  {key: 'Puerto Rico', value: 'Puerto Rico'},
  {key: 'Rhode Island', value: 'Rhode Island'},
  {key: 'South Carolina', value: 'South Carolina'},
  {key: 'South Dakota', value: 'South Dakota'},
  {key: 'Tennessee', value: 'Tennessee'},
  {key: 'Texas', value: 'Texas'},
  {key: 'Utah', value: 'Utah'},
  {key: 'Vermont', value: 'Vermont'},
  {key: 'Virgin Islands', value: 'Virgin Islands'},
  {key: 'Virginia', value: 'Virginia'},
  {key: 'Washington', value: 'Washington'},
  {key: 'West Virginia', value: 'West Virginia'},
  {key: 'Wisconsin', value: 'Wisconsin'},
  {key: 'Wyoming', value: 'Wyoming'},
  {key: 'Alberta', value: 'Alberta'},
  {key: 'British Columbia', value: 'British Columbia'},
  {key: 'Manitoba', value: 'Manitoba'},
  {key: 'New Brunswick', value: 'New Brunswick'},
  {key: 'Newfoundland and Labrador', value: 'Newfoundland and Labrador'},
  {key: 'Northwest Territories', value: 'Northwest Territories'},
  {key: 'Nova Scotia', value: 'Nova Scotia'},
  {key: 'Nunavut', value: 'Nunavut'},
  {key: 'Ontario', value: 'Ontario'},
  {key: 'Prince Edward Island', value: 'Prince Edward Island'},
  {key: 'Quebec', value: 'Quebec'},
  {key: 'Saskatchewan', value: 'Saskatchewan'},
  {key: 'Yukon', value: 'Yukon'}
];

const SnapshotReport = () => {
  const data = Query();
  const {
    firstName,
    lastName,
    email,
    titleLabel,
    titleList,
    enrollmentRangeLabel,
    enrollmentRangeList,
    centerEnrolmentLabel,
    centerEnrolmentList,
    centerName,
    phoneNumber
  } = data.contentfulFormFields;
  const {
    getToKnow,
    getInTouch,
    featureLinks
  } = data;
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const { register, watch, formState: { errors }, handleSubmit } = useForm({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName ,
      email: user.email,
      centerName: user.centerName,
      enrollmentRange: user.enrollmentRange,
      centerEnrolment: user.centerEnrolment,
      stateProvince: user.stateProvince,
      phoneNumber: user.phoneNumber
    }
  });
  
  const [isSticky, setSticky] = useState(false);
  const [isStickToBottom, setStickToBottom] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [isOpen, setOpened] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [showCenterEnrolmentField, setShowCenterEnrolmentField] = useState(false);
  const ref = useRef(null);
  const isModalOpen = isOpened => {
    setOpened(isOpened);
  }
  const onChange = (data) => {
    const isHomeDaycare = data.title === 'Home Daycare Provider';
    setShowCenterEnrolmentField(isHomeDaycare);
  }
  const onSubmit = (data) => {
    dispatch(saveUser(data));
    setSubmit(true);
  };
  const setStickyNav = () => {
    if (ref && ref.current && ref.current.getBoundingClientRect()) {
      setSticky(ref.current.getBoundingClientRect().bottom >= 745 && ref.current.getBoundingClientRect().top <= 170);
      setStickToBottom(ref.current.getBoundingClientRect().top <=0)
    }
  };
  useEffect(() => {
    setPrefill({
      FirstName: user.firstName,
      LastName: user.lastName,
      Email: user.email,
      Title: user.title,
      Company: user.centerName,
      Phone: user.phone,
      stateProvince: user.stateProvince,
      Enrollment__c: user.enrollmentRange,
      Center_Enrolment_Range__c: user.centerEnrolment
    });
  }, [user]);
  useEffect(() => {
    setStickyNav();
    if (isBrowser()) {
      window.addEventListener('scroll', setStickyNav);
      window.addEventListener('resize', setStickyNav);
      // Cleanup listeners
      return () => {
        window.removeEventListener('scroll', () => setStickyNav);
        window.removeEventListener('resize', () => setStickyNav);
      };
    }
  }, []);
  useEffect(() => {
    const isHomeDaycare = user.title === 'Home Daycare Provider';
    setShowCenterEnrolmentField(isHomeDaycare);
  }, [user]);
  useEffect(() => {
    const subscription = watch((value) => onChange(value));
    return () => subscription.unsubscribe();
  }, [watch]);
  return (
    <Main>
      <Section spacing='mt-7 mb-7' id={'download'}>
        <Container>
          <Row className='flex-column flex-lg-row'>
            <div className='col mt-5'>
              <Heading type='h1' className='mb-3'>{'Download the'}<br/>{'2024 Industry Snapshot Report'}</Heading>
              <p className='ff-inter mb-3' style={{fontSize: '24px', lineHeight: '34px'}}>{'Gain valuable insights into the current state of the global childcare industry. Filled with comprehensive data tailored for childcare providers, families, researchers, and policymakers alike.'}</p>
              <OpenModalButton getModalState={isModalOpen} videoId={'1RW4kKD8PwQ'} className='d-flex align-items-center'>
                <StaticImage src={`../../images/snapshot-report/link_play_button.svg`} alt='' width={40} height={40}/>
                <p className='text-dark-teal mb-0 ms-2'>{'Watch introduction video'}</p>
              </OpenModalButton>
              <ModalVideo
                channel={`youtube`}
                isOpen={isOpen}
                videoId={'1RW4kKD8PwQ'}
                onClose={() => setOpened(false)}
              />
            </div>
            <div className='col mt-4'>
              <Form noValidate onSubmit={handleSubmit(onSubmit)} className='position-relative mt-3 p-3 rounded' style={{boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.20)'}}>
                <div className={clsx('d-flex justify-content-center', styles.bookTopMargin)}>
                  <StaticImage src={`../../images/snapshot-report/ebook.png`} alt='' height={331} width={259}/>
                </div>
                <Row>
                  <div className='col'>
                    <Form.Group controlId='firstName' className='mb-3 required'>
                      <Form.Control
                        name='firstName'
                        type='text'
                        required
                        placeholder={firstName}
                        isInvalid={!!errors.firstName}
                        {...register('firstName', { required: 'Please type in your first name.' })}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.firstName?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className='col'>
                    <Form.Group controlId='lastName' className='mb-3 required'>
                      <Form.Control
                        name='lastName'
                        type='text'
                        required
                        placeholder={lastName}
                        isInvalid={!!errors.lastName}
                        {...register('lastName', { required: 'Please type in your last name.' })}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.lastName?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </Row>
                <Form.Group controlId='email' className='mb-3 required'>
                  <Form.Control
                    name='email'
                    type='email'
                    required
                    placeholder={email}
                    isInvalid={!!errors.email}
                    {...register('email', { required: 'An E-mail address is required', pattern: /\S+@\S+\.\S+/ })}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.email?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId='title' className='mb-3 required'>
                  <Form.Select
                    name='title'
                    required
                    isInvalid={!!errors.title}
                    {...register('title', { required: 'Please select a title.', validate: { doesNotEqualBlank: (value) => {
                      return value !== '' || 'Please select a title.';
                    }} })}
                  >
                    <option value=''>{titleLabel}</option>
                    {titleList.map((title, index) => (
                      <option key={index} value={title}>{title}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {errors.title?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId='centerName' className='mb-3 required'>
                  <Form.Control
                    name='centerName'
                    type='text'
                    required
                    placeholder={centerName}
                    isInvalid={!!errors.centerName}
                    {...register('centerName', { required: 'Please type in your center name.' })}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.centerName?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                {!showCenterEnrolmentField &&
                  <Form.Group controlId='enrollmentRange' className='mb-3 required'>
                    <Form.Select
                      name='enrollmentRange'
                      required
                      defaultValue={user.enrollmentRange}
                      isInvalid={!!errors.enrollmentRange}
                      {...register('enrollmentRange', { required: true })}
                    >
                      <option value=''>{enrollmentRangeLabel}</option>
                      {enrollmentRangeList.map((size, index) => (
                        <option key={index} value={size}>{size}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {errors.enrollmentRange?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                }
                {showCenterEnrolmentField &&
                  <Form.Group controlId='centerEnrolment' className='mb-3 required'>
                    <Form.Select
                      name='centerEnrolment'
                      required
                      isInvalid={!!errors.centerEnrolment}
                      {...register('centerEnrolment', { required: true })}
                    >
                      <option value=''>{centerEnrolmentLabel}</option>
                      {centerEnrolmentList.map((size, index) => (
                        <option key={index} value={size}>{size}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {errors.centerEnrolment?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                }
                <Form.Group controlId='stateProvince' className='mb-3 required'>
                  <Form.Select
                    name='stateProvince'
                    required
                    isInvalid={!!errors.stateProvince}
                    {...register('stateProvince', { required: 'State/Province is required.' })}
                  >
                    <option value=''>{'State/Province'}</option>
                    {states.map((state, index) => (
                      <option key={index} value={state.value}>{state.key}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {errors.stateProvince?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId='phoneNumber' className='mb-3 required'>
                  <Form.Control
                    name='phoneNumber'
                    type='tel'
                    required
                    placeholder={phoneNumber}
                    isInvalid={!!errors.phone}
                    {...register('phone', { required: 'A phone number is required' })}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.phone?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Button
                  type='submit'
                  variant='primary'
                  addClass='w-100'
                  copy={submit ? 'Please wait...' : 'Download full report'}
                  disabled={submit}
                />
              </Form>
            </div>
          </Row>
        </Container>
        {submit &&
          <div className='sr-only' aria-hidden>
            <MarketoForm formId={'1556'} prefill={prefill} submit={submit}/>
          </div>
        }
      </Section>
      <Container>
        <Row>
          <div className='col-lg-9'>
            <Section>
              <Row>
                <Heading type='h2' className='h3' id={'introduction'}>{'Introduction'}</Heading>
                <p>
                  <i>{'Building the Future of Early Childhood Education: 2024 Industry Snapshot Report'}</i>{' is a comprehensive snapshot report crafted from the voices of over 1300 early childhood professionals.'}<br/><br/>
                  {'This report is tailored to provide actionable insights that empower educators in building, managing, and growing high-quality early childhood programs. Lillio (formerly HiMama) created this report to equip educators with the tools and information needed to elevate program quality and improve outcomes for children in 2024 and beyond. Here, you can find key highlights and takeaways from each section within the report.'}
                </p>
              </Row>
            </Section>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'participants'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'participants'}>{'Survey participants'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'participants'}>
                <Section spacing='mb-0'>
                  <Row className='flex-column flex-lg-row'>
                    <div className='col'>
                      <div className='bg-light-grey-1 rounded p-3 text-center'>
                        <p className='fw-medium mb-1'>{'Participants\' roles'}</p>
                        <div className='mb-3'>
                          <div className='position-relative px-3'>
                            <img src={ParticipantsRoles} alt='' style={{width: '80%'}}/>
                            <p className='position-absolute mb-0' style={{top:'70%', left: '10%'}}>{'23%'}</p>
                            <p className='position-absolute mb-0' style={{top: '20%', right: '10%'}}>{'36%'}</p>
                            <p className='position-absolute mb-0' style={{top:'75%', right: '10%'}}>{'18%'}</p>
                          </div>
                        </div>
                        <Row className='row-cols-2 gy-3 text-start'>
                          <div className='d-flex align-items-center'>
                            <div className='bg-teal rounded' style={{minHeight: 30, minWidth: 30}}/>
                            <p className='ms-3 mb-0 fs-12'>{'center owners'}</p>
                          </div>
                          <div className='d-flex align-items-center'>
                            <div className='bg-red rounded' style={{minHeight: 30, minWidth: 30}}/>
                            <p className='ms-3 mb-0 fs-12'>{'teachers'}</p>
                          </div>
                          <div className='d-flex align-items-center'>
                            <div className='bg-yellow rounded' style={{minHeight: 30, minWidth: 30}}/>
                            <p className='ms-3 mb-0 fs-12'>{'senior level roles'}</p>
                          </div>
                        </Row>
                      </div>
                    </div>
                    <div className='col'>
                      <div className='bg-light-grey-1 rounded p-3 text-center'>
                        <p className='fw-medium mb-1'>{'Participants\' locations'}</p>
                        <div className='mb-3'>
                          <div className='position-relative px-3'>
                            <img src={ParticipantsLocations} alt='' style={{width: '80%'}}/>
                            <p className='position-absolute mb-0' style={{top:'70%', left: '10%'}}>{'59%'}</p>
                            <p className='position-absolute mb-0' style={{top: '20%', right: '10%'}}>{'28%'}</p>
                            <p className='position-absolute mb-0' style={{top:'60%', right: '10%'}}>{'13%'}</p>
                          </div>
                        </div>
                        <Row className='row-cols-2 gy-3 text-start'>
                          <div className='d-flex align-items-center'>
                            <div className='bg-teal rounded' style={{minHeight: 30, minWidth: 30}}/>
                            <p className='ms-3 mb-0 fs-12'>{'United States'}</p>
                          </div>
                          <div className='d-flex align-items-center'>
                            <div className='bg-red rounded' style={{minHeight: 30, minWidth: 30}}/>
                            <p className='ms-3 mb-0 fs-12'>{'International'}</p>
                          </div>
                          <div className='d-flex align-items-center'>
                            <div className='bg-yellow rounded' style={{minHeight: 30, minWidth: 30}}/>
                            <p className='ms-3 mb-0 fs-12'>{'Canada'}</p>
                          </div>
                        </Row>
                      </div>
                    </div>
                  </Row>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'state'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'state'}>{'State of childcare for 2024'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'state'}>
                <Section spacing='mb-0'>
                  <div className='col-auto'>
                    <StaticImage src={`../../images/snapshot-report/state_1.png`} alt='' className='mb-5'/>
                    <Heading type='h4'>{'Budget and Income Assessment:'}</Heading>
                    <p className='mb-5'>{'In terms of budget, we are seeing that 93% of centers are operating within a tight to limited budget on an annual basis. In terms of income, 80% of centers reported a steady but generally tight income stream.'}</p>
                    <Heading type='h4'>{'Staffing'}</Heading>
                    <StaticImage src={`../../images/snapshot-report/state_2.png`} alt='' className='mb-5'/>
                    <Heading type='h4'>{'Salary Insights'}</Heading>
                    <Row className='justify-content-center mb-5'>
                      <div className='col-auto'>
                        <StaticImage src={`../../images/snapshot-report/insights_1.png`} alt='' width={242} height={304}/>
                      </div>
                      <div className='col-auto'>
                        <StaticImage src={`../../images/snapshot-report/insights_2.png`} alt='' width={242} height={304}/>
                      </div>
                    </Row>
                    <Heading type='h4' className='mb-5'>{'Tuition Rates Across Classrooms'}</Heading>
                    <Row className='row-cols-lg-3 gy-5 mt-3 mx-xl-3'>
                      <div className='d-flex'>
                        <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-4 text-center position-relative'>
                          <div className='bg-yellow rounded p-2 mb-3 mx-2 position-absolute' style={{top: -25, left: 0, right: 0}}>
                            <p className='fw-medium mb-0'>{'Infant classroom'}</p>
                          </div>
                          <p>{'Over half of centers charge '}<span className='fw-medium'>{'$2000'}</span>{' or more monthly for families seeking infant care.'}</p>
                        </div>
                      </div>
                      <div className='d-flex'>
                        <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-4 text-center position-relative'>
                          <div className='bg-green rounded p-2 mb-3 mx-2 position-absolute' style={{top: -25, left: 0, right: 0}}>
                            <p className='fw-medium mb-0'>{'Toddler classroom'}</p>
                          </div>
                          <p>{'The majority, approximately '}<span className='fw-medium'>{'56%'}</span>{', charge between '}<span className='fw-medium'>{'$1000'}</span>{' and '}<span className='fw-medium'>{'$1500'}</span>{' per month for families seeking toddler care.'}</p>
                        </div>
                      </div>
                      <div className='d-flex'>
                        <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-4 text-center position-relative'>
                          <div className='bg-red rounded p-2 mb-3 mx-2 position-absolute' style={{top: -25, left: 0, right: 0}}>
                            <p className='fw-medium mb-0'>{'Preschool classroom'}</p>
                          </div>
                          <p>{'A significant portion, nearly '}<span className='fw-medium'>{'58%'}</span>{', charge between '}<span className='fw-medium'>{'$1000'}</span>{' and '}<span className='fw-medium'>{'$1500'}</span>{' per month.'}</p>
                        </div>
                      </div>
                      <div className='d-flex col-lg-6'>
                        <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-4 text-center position-relative'>
                          <div className='bg-teal rounded p-2 mb-3 mx-2 position-absolute' style={{top: -25, left: 0, right: 0}}>
                            <p className='fw-medium mb-0'>{'School-aged program'}</p>
                          </div>
                          <p>{'The average monthly cost for centers that operate a school-aged program was less than '}<span className='fw-medium'>{'$400'}</span>{' per month for families.'}</p>
                        </div>
                      </div>
                      <div className='d-flex col-lg-6'>
                        <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-4 text-center position-relative'>
                          <div className='bg-light-yellow rounded p-2 mb-3 mx-2 position-absolute' style={{top: -25, left: 0, right: 0}}>
                            <p className='fw-medium mb-0'>{'Before and after school programs'}</p>
                          </div>
                          <p>{'Half of the centers that operate a before and after school program charge less than '}<span className='fw-medium'>{'$400'}</span>{' per month.'}</p>
                        </div>
                      </div>
                    </Row>
                  </div>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'strategies'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'strategies'}>{'Center management and operations strategies'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'strategies'}>
                <Section spacing='mb-0'>
                  <Heading type='h4' className='mb-5'>{'Tuition Billing and Payments'}</Heading>
                  <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 text-center mb-5 mx-lg-5'>
                    <p>{'How do you keep track of billing and payments at your center?'}</p>
                    <Row className='row-cols-lg-2 row-cols-xl-4 g-3'>
                      <div className='d-flex'>
                        <div className='bg-teal rounded p-3 w-100'>
                          <img src={Billing} alt='' width={50} height={50}/>
                          <p className='mt-3 mb-0'>{'35% use online billing'}</p>
                        </div>
                      </div>
                      <div className='d-flex'>
                        <div className='bg-yellow rounded p-3 w-100'>
                          <img src={Accounting} alt='' width={50} height={50}/>
                          <p className='mt-3 mb-0'>{'22% use accounting software'}</p>
                        </div>
                      </div>
                      <div className='d-flex'>
                        <div className='bg-green rounded p-3 w-100'>
                          <img src={Pen} alt='' width={50} height={50}/>
                          <p className='mt-3 mb-0'>{'24% use pen-and-paper'}</p>
                        </div>
                      </div>
                      <div className='d-flex'>
                        <div className='bg-red rounded p-3 w-100'>
                          <img src={Other} alt='' width={50} height={50}/>
                          <p className='mt-3 mb-0'>{'19% use other methods'}</p>
                        </div>
                      </div>
                    </Row>
                  </div>
                  <Heading type='h4'>{'Budget and resource allocation'}</Heading>
                  <p>{'For classroom supplies, we are seeing that a combined 48% of centers dedicate 10% or less of their monthly budget to updating classroom supplies and resources. In regards to staff salaries, remarkably, across different sizes of programs, there is a consistent trend in budget allocation for staff salaries. Whether it is a home daycare, single-location center, or a program with multiple locations, the average percentage allocated for staff salaries falls within 50%-70%.'}</p>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'methodologies'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'methodologies'}>{'Common teaching methodologies'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'methodologies'}>
                <Section spacing='mb-0'>
                  <p className='mb-0'>{'The most common teaching methodology shared by educators was play-based learning. A majority (63%) of educators prioritize play-based learning in their childcare programs.'}</p>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'transformation'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'transformation'}>{'Digital Transformation Plans for 2024'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'transformation'}>
                <Section spacing='mb-0'>
                  <p className='mb-0'>{'An overwhelming 80% of childcare professionals plan to adopt or continue using digital tools in 2024, emphasizing their importance in improving communication and operational efficiency.'}<br/>{'Family Communication: Digital communication apps, especially childcare management apps like Lillio, are preferred by 48% of centers for family communication.'}</p>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'trends'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'trends'}>{'Curriculum Trends'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'trends'}>
                <Section spacing='mb-0'>
                  <p>{'65% of centers prefer a hands-on approach and opt to create their own curriculum. Meanwhile, 12% of centers receive physical curriculum kits delivered to their doorstep, 7% of responses, leverage digital curriculum subscriptions and 6%, choose to collaborate with curriculum consultants.'}</p>
                  <Heading type='h4'>{'Trending curriculum topics'}</Heading>
                  <div className='col-auto text-center my-3 px-lg-3 position-relative'>
                    <StaticImage src={`../../images/snapshot-report/trending_1.png`} alt=''/>
                    <img src={YellowCircleStar} alt='' className='position-absolute d-none d-lg-block' style={{top: 300, right: -30}}/>
                  </div>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'development'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'development'}>{'Importance of continuous professional development'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'development'}>
                <Section spacing='mb-0'>
                  <p className='mb-3'>{'A significant majority (86%) of professionals believe it is "extremely important," or “very important” indicating a widespread acknowledgment of its profound impact on the quality of education. This collective commitment underscores the industry\'s dedication to providing the best care for children.'}</p>
                  <Heading type='h4'>{'Professional Development Frequency and Methods:'}</Heading>
                  <div className='bg-light-grey-1 rounded p-3 mb-3 mx-lg-5'>
                    <p className='fw-medium text-center' style={{width: '90%'}}>{'How frequently does the centers\' staff participate in professional training?'}</p>
                    <div className='d-flex text-center w-100'>
                      <div style={{width: '40%'}}>
                        <p className='mb-0'>{'40%'}</p>
                      </div>
                      <div className='mb-0' style={{width: '13%'}}>
                        <p>{'13%'}</p>
                      </div>
                      <div className='mb-0' style={{width: '22%'}}>
                        <p>{'22%'}</p>
                      </div>
                      <div className='mb-0' style={{width: '15%'}}>
                        <p>{'15%'}</p>
                      </div>
                    </div>
                    <div className='d-flex w-100 mb-3' style={{height: 60}}>
                      <div className='bg-teal' style={{width: '40%'}}/>
                      <div className='bg-yellow' style={{width: '13%'}}/>
                      <div className='bg-green' style={{width: '22%'}}/>
                      <div className='bg-red' style={{width: '15%'}}/>
                    </div>
                    <Row className='row-cols-2 gy-3'>
                      <div className='d-flex align-items-center'>
                        <div className='bg-teal rounded' style={{minHeight: 30, minWidth: 30}}/>
                        <p className='ms-3 mb-0'>{'monthly'}</p>
                      </div>
                      <div className='d-flex align-items-center'>
                        <div className='bg-green rounded' style={{minHeight: 30, minWidth: 30}}/>
                        <p className='ms-3 mb-0'>{'quarterly'}</p>
                      </div>
                      <div className='d-flex align-items-center'>
                        <div className='bg-yellow rounded' style={{minHeight: 30, minWidth: 30}}/>
                        <p className='ms-3 mb-0'>{'semi-annually'}</p>
                      </div>
                      <div className='d-flex align-items-center'>
                        <div className='bg-red rounded' style={{minHeight: 30, minWidth: 30}}/>
                        <p className='ms-3 mb-0'>{'annually'}</p>
                      </div>
                    </Row>
                  </div>
                  <p>{'Online courses dominate as the preferred method (79%), offering accessibility and diverse resources for educators.'}</p>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'priorities'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'priorities'}>{'Top priorities for 2024'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'priorities'}>
                <Section spacing='mb-0'>
                  <Row className='row-cols-lg-3 gy-5 mt-3'>
                    <div className='d-flex'>
                      <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-5 text-center position-relative'>
                        <div className='bg-teal rounded-circle position-relative mb-3 mx-auto' style={{width: 72, height: 72, marginTop: -110}}>
                          <img src={Increase} alt='' height={30} width={30} className='position-absolute' style={{top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}/>
                        </div>
                        <p className='fw-medium'>{'Increasing enrollment and revenue'}</p>
                        <p>{'This was identified as the top priority, with 31% of respondents considering it of paramount importance.'}</p>
                      </div>
                    </div>
                    <div className='d-flex'>
                      <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-5 text-center position-relative'>
                        <div className='bg-yellow rounded-circle position-relative mb-3 mx-auto' style={{width: 72, height: 72, marginTop: -110}}>
                          <img src={Message} alt='' height={30} width={30} className='position-absolute' style={{top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}/>
                        </div>
                        <p className='fw-medium'>{'Family engagement and communication'}</p>
                        <p>{'Close behind, 28% of respondents ranked this as their primary concern, emphasizing the critical role of effective communication in creating a supportive educational environment.'}</p>
                      </div>
                    </div>
                    <div className='d-flex'>
                      <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-5 text-center'>
                        <div className='bg-green rounded-circle position-relative mb-3 mx-auto' style={{width: 72, height: 72, marginTop: -110}}>
                          <img src={Apple} alt='' height={30} width={30} className='position-absolute' style={{top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}/>
                        </div>
                        <p className='fw-medium'>{'Teacher retention and leveling up their skills'}</p>
                        <p>{'This was identified as a significant concern, with 21% of respondents considering it a top priority. This reflects an acknowledgment of both staffing challenges and the critical responsibility educators have in delivering quality learning experiences.'}</p>
                      </div>
                    </div>
                    <div className='d-flex col-lg-6'>
                      <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-5 text-center'>
                        <div className='bg-red rounded-circle position-relative mb-3 mx-auto' style={{width: 72, height: 72, marginTop: -110}}>
                          <img src={Little} alt='' height={30} width={30} className='position-absolute' style={{top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}/>
                        </div>
                        <p className='fw-medium'>{'Enhanced programming to support child development'}</p>
                        <p>{'This aspect garnered attention as well, with 16% ranking it as a top priority. This indicates a strong commitment to providing high-quality educational experiences that foster holistic child development.'}</p>
                      </div>
                    </div>
                    <div className='d-flex col-lg-6'>
                      <div className='border border-3 border-dark-grey-4 bg-white rounded p-3 pt-5 text-center'>
                        <div className='bg-light-teal rounded-circle position-relative mb-3 mx-auto' style={{width: 72, height: 72, marginTop: -110}}>
                          <img src={City} alt='' height={30} width={30} className='position-absolute' style={{top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}/>
                        </div>
                        <p className='fw-medium'>{'Modernizing the center to stay competitive in 2024'}</p>
                        <p>{'This was ranked relatively lower in terms of priority, with 3% ranking it as their number one priority, though notably 67% of respondents still consider it a top five priority for the 2024 year.'}</p>
                      </div>
                    </div>
                  </Row>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'future'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'future'}>{'Optimism about the future'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'future'}>
                <Section spacing='mb-0'>
                  <p className='mb-0'>{'An optimistic outlook prevails, with 60% expressing combined "extreme" and "very optimistic" sentiments. Even those somewhat optimistic (28%) indicate hope for the future, highlighting the profound value and excitement associated with the role of early childhood professionals.'}</p>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'challenges'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'challenges'}>{'Challenges in 2024'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'challenges'}>
                <Section spacing='mb-0'>
                  <p className='mb-0'>{'Identified challenges include the need for increased staff, improving professional standards, fostering positive relationships with children and parents, and a desire for continued growth within the profession. Addressing these challenges requires staying updated, embracing technology, fostering support networks, and prioritizing self-care.'}</p>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'educators'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'educators'}>{'Educators\' excitement for 2024'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'educators'}>
                <Section spacing='mb-0'>
                  <p className='mb-0'>{'Heading into 2024, educators express excitement about watching children learn and grow, incorporating new active learning approaches, engaging in professional development, improving parent partnerships, receiving recognition and advocacy for the field, integrating technology, witnessing community growth, and developing innovative curricula. These themes reflect educators\' dedication to creating positive and impactful educational environments.'}</p>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
            <Accordion className='mb-5'>
              <ContextAwareToggle eventKey={'leaders'} arrow='left'>
                <Heading type='h3' className='mb-0' id={'leaders'}>{'What educators need from their leaders 2024'}</Heading>
              </ContextAwareToggle>
              <Accordion.Collapse eventKey={'leaders'}>
                <Section spacing='mb-0'>
                  <p>{'In this year\'s survey we interviewed educators to ask them '}<strong>{'what they are excited about, what challenges they expect, and what they need from their leader in 2024.'}</strong></p>
                  <p>{'In these interviews, educators expressed excitement about implementing new technology and curriculum, but anticipate challenges related to staff shortages and the need for high-quality curriculum in their classrooms. They seek support from leadership teams in the form of time-saving  resources such as educational apps, better pay and recognition, flexibility, encouragement, and support for pursuing higher education. Additionally, educators demand more inclusive resources, additional staff, and greater resources for children with disabilities.'}</p>
                  <p>{'Looking for more support with staff retention, budgeting, marketing and mental health as you embark on 2024 and beyond?  It is all in Building the Future of Early Childhood Education: 2024 Industry Snapshot Report!'}</p>
                  <p>{'Download the full report to access even more data, strategies and insights.'}</p>
                  <AnchorLink
                    to={`#download`}
                    title={'Download full report'}
                    className={'btn btn-primary mb-4'}
                  >
                    {'Download full report'}
                  </AnchorLink>
                  <p>{'At Lillio, we are proud to provide the building blocks to help you in your mission to provide children and families with high-quality childcare.'}</p>
                  <p>{'- The Lillio Team'}</p>
                </Section>
              </Accordion.Collapse>  
            </Accordion>
          </div>
          <div className={clsx(`d-none d-lg-flex col-3 position-relative`, {'align-items-end':isStickToBottom})} ref={ref}>
            <nav id={`stickyNav`} className={clsx('d-flex flex-column', stickyNav, {[stickyNavFixed]:isSticky})}>
              <ul className={`list-unstyled mb-3 fs-16`}>
                <li>
                  <AnchorLink
                    to={`#introduction`}
                    title={'Introduction'}
                    className={navLinks}
                  >
                    {'Introduction'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#participants`}
                    title={'Survey participants'}
                    className={navLinks}
                  >
                    {'Survey participants'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#state`}
                    title={'State of childcare'}
                    className={navLinks}
                  >
                    {'State of childcare'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#strategies`}
                    title={'Center management and operation strategies'}
                    className={navLinks}
                  >
                    {'Center management and operation strategies'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#methodologies`}
                    title={'Common teaching methodologies'}
                    className={navLinks}
                  >
                    {'Common teaching methodologies'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#trends`}
                    title={'Curriculum trends'}
                    className={navLinks}
                  >
                    {'Curriculum trends'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#development`}
                    title={'Importance of continuous professional development'}
                    className={navLinks}
                  >
                    {'Importance of continuous professional development'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#priorities`}
                    title={'Top priorities for 2024'}
                    className={navLinks}
                  >
                    {'Top priorities for 2024'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#future`}
                    title={'Optimism about the future'}
                    className={navLinks}
                  >
                    {'Optimism about the future'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#challenges`}
                    title={'Challenges in 2024'}
                    className={navLinks}
                  >
                    {'Challenges in 2024'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#educators`}
                    title={'Educators’ excitement for 2024'}
                    className={navLinks}
                  >
                    {'Educators’ excitement for 2024'}
                  </AnchorLink>
                </li>
                <li>
                  <AnchorLink
                    to={`#leaders`}
                    title={'What educators need from their leaders in 2024'}
                    className={navLinks}
                  >
                    {'What educators need from their leaders in 2024'}
                  </AnchorLink>
                </li>
              </ul>
              <AnchorLink
                to={`#download`}
                title={'Download full report'}
                className={'btn btn-primary mb-4'}
              >
                {'Download full report'}
              </AnchorLink>
            </nav>
          </div>
        </Row>
      </Container>
      <Blocks data={getToKnow}/>
      <Blocks data={featureLinks}/>
      <Blocks data={getInTouch}/>
    </Main>
  )
}

export default SnapshotReport

export const Head = ({ location }) => {
  const data = Query();
  const { metaImage } = data;
  return (
    <SEO
      index={true}
      titleTag={'Industry Snapshot Report 2024'}
      metaDescription={'Gain valuable insights into the current state of the global childcare industry. Download the Industry Snapshot Report today.'}
      metaImage={metaImage}
      location={location}
    />
  )
}